import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

export default function SecurityPage() {
  const { profile } = useAuth();

  const [activeTab, setActiveTab] = useState<'overview' | '2fa' | 'sessions' | 'apikeys'>('overview');

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // 2FA state
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // Mock sessions
  const [sessions] = useState([
    { id: '1', ip: '187.45.123.45', location: 'São Paulo, Brasil', device: 'Chrome 132 / Windows 11', current: true, lastActive: 'Agora', created: '12/02/2026' },
    { id: '2', ip: '201.17.89.210', location: 'Rio de Janeiro, Brasil', device: 'Safari / macOS 15', current: false, lastActive: '2 horas atrás', created: '05/01/2026' },
    { id: '3', ip: '45.178.12.90', location: 'Curitiba, Brasil', device: 'Chrome 131 / Windows 10', current: false, lastActive: '3 dias atrás', created: '20/12/2025' },
  ]);

  // API Keys
  const [apiKeys] = useState([
    { id: '1', name: 'API Key Produção', prefix: 'wzp_live_', last4: 'xK9m', created: '10/01/2026', lastUsed: 'Hoje', scopes: ['read', 'send', 'contacts'] },
    { id: '2', name: 'API Key Desenvolvimento', prefix: 'wzp_test_', last4: 'aB3c', created: '15/11/2025', lastUsed: '3 dias atrás', scopes: ['read'] },
  ]);

  const handlePasswordChange = async () => {
    setPasswordMsg(null);

    if (newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'A nova senha deve ter pelo menos 8 caracteres.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'As senhas não coincidem.' });
      return;
    }

    setChangingPassword(true);

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    setChangingPassword(false);

    if (error) {
      setPasswordMsg({ type: 'error', text: error.message === 'New password should be different from the old password.' ? 'A nova senha deve ser diferente da senha atual.' : error.message });
    } else {
      setPasswordMsg({ type: 'success', text: 'Senha alterada com sucesso!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const revokeSession = async (sessionId: string) => {
    // In a real implementation, this would call Supabase auth admin API
    alert(`Sessão ${sessionId} seria revogada. Implemente via Edge Function com service_role.`);
  };

  const tabs = [
    { key: 'overview', label: 'Visão Geral', icon: 'ri-shield-line' },
    { key: '2fa', label: 'Autenticação 2 Fatores', icon: 'ri-smartphone-line' },
    { key: 'sessions', label: 'Sessões Ativas', icon: 'ri-computer-line' },
    { key: 'apikeys', label: 'API Keys', icon: 'ri-key-2-line' },
  ];

  const inputClass = "w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all";

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-heading font-bold text-foreground-950">Segurança</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Gerencie a segurança da sua conta: senha, autenticação, sessões e chaves de API
        </p>
      </div>

      {/* Tab Switcher */}
      <div className="inline-flex bg-background-100 rounded-full p-1 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === tab.key
                ? 'bg-white text-foreground-950 shadow-sm'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${tab.icon} text-base`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── OVERVIEW ─── */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* Status Card */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-4">Status de Segurança</h3>
            <div className="space-y-3">
              {[
                { label: 'Senha', status: 'ok', detail: 'Definida', icon: 'ri-lock-password-line' },
                { label: 'Autenticação 2 Fatores', status: twoFAEnabled ? 'ok' : 'warn', detail: twoFAEnabled ? 'Ativada' : 'Não configurada', icon: 'ri-smartphone-line' },
                { label: 'Sessões Ativas', status: 'ok', detail: `${sessions.length} sessões`, icon: 'ri-computer-line' },
                { label: 'API Keys', status: apiKeys.length > 0 ? 'ok' : 'warn', detail: `${apiKeys.length} chave${apiKeys.length !== 1 ? 's' : ''}`, icon: 'ri-key-2-line' },
                { label: 'IPs Conectados', status: 'ok', detail: 'Monitorando', icon: 'ri-earth-line' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-background-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                      <i className={`${item.icon} text-sm text-foreground-500`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{item.label}</p>
                      <p className="text-xs text-foreground-500">{item.detail}</p>
                    </div>
                  </div>
                  <div className={`w-2.5 h-2.5 rounded-full ${item.status === 'ok' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Quick access */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button onClick={() => setActiveTab('2fa')} className="bg-white rounded-xl border border-background-200 p-4 text-left hover:border-primary-300 transition-colors cursor-pointer group">
              <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center mb-2.5">
                <i className="ri-smartphone-line text-primary-600" />
              </div>
              <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">Configurar 2FA</p>
              <p className="text-xs text-foreground-500 mt-0.5">Adicione uma camada extra de proteção</p>
            </button>
            <button onClick={() => setActiveTab('sessions')} className="bg-white rounded-xl border border-background-200 p-4 text-left hover:border-primary-300 transition-colors cursor-pointer group">
              <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center mb-2.5">
                <i className="ri-computer-line text-accent-600" />
              </div>
              <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">Sessões</p>
              <p className="text-xs text-foreground-500 mt-0.5">{sessions.length} sessões ativas</p>
            </button>
            <button onClick={() => setActiveTab('apikeys')} className="bg-white rounded-xl border border-background-200 p-4 text-left hover:border-primary-300 transition-colors cursor-pointer group">
              <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center mb-2.5">
                <i className="ri-key-2-line text-secondary-600" />
              </div>
              <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">API Keys</p>
              <p className="text-xs text-foreground-500 mt-0.5">{apiKeys.length} chaves configuradas</p>
            </button>
          </div>
        </div>
      )}

      {/* ─── PASSWORD ─── */}
      {activeTab === '2fa' && (
        <div className="space-y-4">
          {/* Password change */}
          <div className="bg-white rounded-xl border border-background-200 p-5 space-y-4">
            <h3 className="text-sm font-heading font-semibold text-foreground-950">Alterar Senha</h3>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nova Senha</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Mínimo 8 caracteres" className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Confirmar Nova Senha</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repita a nova senha" className={inputClass} />
            </div>
            {passwordMsg && (
              <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${passwordMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                <i className={passwordMsg.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
                {passwordMsg.text}
              </div>
            )}
            <button
              onClick={handlePasswordChange}
              disabled={changingPassword || !newPassword || !confirmPassword}
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {changingPassword ? <><i className="ri-loader-4-line animate-spin" /> Alterando...</> : <><i className="ri-lock-line" /> Alterar Senha</>}
            </button>
          </div>

          {/* 2FA Section */}
          <div className="bg-white rounded-xl border border-background-200 p-5 space-y-4">
            <div>
              <h3 className="text-sm font-heading font-semibold text-foreground-950">Autenticação de 2 Fatores (2FA)</h3>
              <p className="text-xs text-foreground-500 mt-1">Adicione uma camada extra de segurança usando um app autenticador como Google Authenticator ou Authy.</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-background-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center">
                  <i className="ri-smartphone-line text-xl text-accent-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-800">Google Authenticator / Authy</p>
                  <p className="text-xs text-foreground-500">{twoFAEnabled ? '2FA ativado' : 'Não configurado'}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setTwoFAEnabled(!twoFAEnabled); if (!twoFAEnabled) setShowQR(true); else setShowQR(false); }}
                className={`relative w-10 h-5.5 rounded-full transition-colors cursor-pointer ${twoFAEnabled ? 'bg-emerald-500' : 'bg-background-300'}`}
              >
                <span className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform ${twoFAEnabled ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
              </button>
            </div>

            {showQR && (
              <div className="p-5 bg-background-50 rounded-xl border border-dashed border-accent-300 text-center">
                <p className="text-sm font-medium text-foreground-700 mb-4">Escaneie o QR Code com seu app autenticador</p>
                <div className="w-48 h-48 mx-auto bg-white rounded-xl border border-background-200 flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-qr-code-line text-5xl text-foreground-300 block mb-2" />
                    <span className="text-xs text-foreground-400">QR Code simulado</span>
                  </div>
                </div>
                <p className="text-xs text-foreground-500 mt-4 mb-2">Ou use este código manualmente:</p>
                <code className="inline-block px-4 py-2 bg-white rounded-lg text-sm font-mono text-foreground-800 border border-background-200">
                  WZAP-SECRET-KEY-2FA-EXAMPLE
                </code>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => { setShowQR(false); setTwoFAEnabled(true); }}
                    className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Confirmar Ativação 2FA
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Tokens section */}
          <div className="bg-white rounded-xl border border-background-200 p-5 space-y-3">
            <h3 className="text-sm font-heading font-semibold text-foreground-950">Tokens de Acesso</h3>
            <p className="text-xs text-foreground-500">Tokens são usados por integrações e aplicações terceiras para autenticar requisições à API.</p>
            <div className="space-y-2">
              {[
                { name: 'Token de Acesso Principal', created: '10/01/2026', lastUsed: 'Hoje' },
                { name: 'Token Integração CRM', created: '15/02/2026', lastUsed: '2 dias atrás' },
              ].map((token, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-background-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                      <i className="ri-key-2-line text-sm text-foreground-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{token.name}</p>
                      <p className="text-xs text-foreground-400">Criado: {token.created} · Último uso: {token.lastUsed}</p>
                    </div>
                  </div>
                  <button className="text-xs text-red-500 hover:text-red-700 cursor-pointer font-medium whitespace-nowrap">Revogar</button>
                </div>
              ))}
            </div>
            <button className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer mt-2">
              <i className="ri-add-line" /> Gerar Novo Token
            </button>
          </div>
        </div>
      )}

      {/* ─── SESSIONS ─── */}
      {activeTab === 'sessions' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-heading font-semibold text-foreground-950">Sessões Ativas</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Dispositivos e locais onde sua conta está logada</p>
              </div>
              <button
                onClick={() => alert('Implementar: revogar todas as sessões via Supabase Auth Admin API (service_role)')}
                className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Revogar Todas
              </button>
            </div>

            <div className="space-y-2">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className={`flex items-start justify-between p-3 rounded-lg transition-colors ${
                    session.current ? 'bg-primary-50/50 border border-primary-200' : 'bg-background-50 hover:bg-background-100'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      session.current ? 'bg-primary-100' : 'bg-background-100'
                    }`}>
                      <i className={`ri-computer-line text-sm ${session.current ? 'text-primary-600' : 'text-foreground-500'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-foreground-800">{session.device}</p>
                        {session.current && (
                          <span className="px-1.5 py-0.5 bg-primary-100 text-primary-700 text-[10px] font-bold rounded-full whitespace-nowrap">ATUAL</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-foreground-500 flex-wrap">
                        <span className="flex items-center gap-1"><i className="ri-map-pin-line text-[10px]" />{session.location}</span>
                        <span className="flex items-center gap-1"><i className="ri-earth-line text-[10px]" />IP: {session.ip}</span>
                      </div>
                      <p className="text-[10px] text-foreground-400 mt-1.5">
                        Última atividade: {session.lastActive} · Iniciada: {session.created}
                      </p>
                    </div>
                  </div>
                  {!session.current && (
                    <button
                      onClick={() => revokeSession(session.id)}
                      className="px-2.5 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                    >
                      Revogar
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* IPs Conectados */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-4">IPs Conectados</h3>
            <div className="space-y-2">
              {[
                { ip: '187.45.123.45', location: 'São Paulo, Brasil', isp: 'Vivo Fibra', sessions: 1, lastSeen: 'Agora' },
                { ip: '201.17.89.210', location: 'Rio de Janeiro, Brasil', isp: 'Claro NET', sessions: 1, lastSeen: '2 horas atrás' },
                { ip: '45.178.12.90', location: 'Curitiba, Brasil', isp: 'Copel Telecom', sessions: 1, lastSeen: '3 dias atrás' },
              ].map((ip, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-background-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                      <i className="ri-earth-line text-sm text-foreground-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 font-mono">{ip.ip}</p>
                      <p className="text-xs text-foreground-400">{ip.location} · {ip.isp}</p>
                    </div>
                  </div>
                  <span className="text-xs text-foreground-400">{ip.lastSeen}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── API KEYS ─── */}
      {activeTab === 'apikeys' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-heading font-semibold text-foreground-950">Chaves de API</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Gerencie chaves para acesso programático à API do WZAP Pro</p>
              </div>
              <button className="px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-add-line" /> Nova Chave
              </button>
            </div>

            {apiKeys.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-key-2-line text-xl text-foreground-400" />
                </div>
                <p className="text-sm text-foreground-500">Nenhuma chave de API</p>
                <p className="text-xs text-foreground-400 mt-1">Crie uma chave para integrar com sistemas externos</p>
              </div>
            ) : (
              <div className="space-y-2">
                {apiKeys.map((key) => (
                  <div key={key.id} className="p-4 bg-background-50 rounded-xl border border-background-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-foreground-800">{key.name}</p>
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            key.prefix === 'wzp_live_' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {key.prefix === 'wzp_live_' ? 'Produção' : 'Teste'}
                          </span>
                        </div>
                        <code className="text-xs text-foreground-500 font-mono mt-1 block">
                          {key.prefix}****{key.last4}
                        </code>
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-foreground-400">
                          <span>Criada: {key.created}</span>
                          <span>Último uso: {key.lastUsed}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer" title="Copiar">
                          <i className="ri-file-copy-line text-sm" />
                        </button>
                        <button className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title="Revogar">
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {key.scopes.map((scope) => (
                        <span key={scope} className="px-2 py-0.5 bg-white border border-background-200 rounded-md text-[10px] font-medium text-foreground-600">
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rate Limits Info */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-3">Limites de Rate</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: 'Requisições/min', value: '60' },
                { label: 'Mensagens/min', value: '30' },
                { label: 'Webhooks/min', value: '120' },
              ].map((item) => (
                <div key={item.label} className="p-3 bg-background-50 rounded-lg text-center">
                  <p className="text-2xl font-heading font-bold text-foreground-950">{item.value}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}